import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FssaiGeneratedTagService } from 'app/service/fssai-generated-tag.service';
import { MasterService } from 'app/service/master.service';
import { UserService } from 'app/service/user.service';
import { environment } from 'environments/environment';
import {
  SUPER_ADMIN_RECIPE_METADATA_INCLUDE,
  SUPER_ADMIN_RECIPE_STATUS_OPTIONS,
  SuperAdminBulkAssignment,
  SuperAdminBulkImportItem,
  SuperAdminBulkLookups,
  SuperAdminBulkParseResult,
  SuperAdminBulkPreviewRow,
} from './super-admin-recipe-bulk-upload.models';
import {
  applyGlobalStatusToRows,
  downloadSuperAdminRecipeTemplate,
  SuperAdminRecipeTemplateFormat,
  mapMetadataToLookups,
  rebuildSuperAdminRecipePayload,
  refreshSuperAdminParseCounts,
  hydrateAllSuperAdminBulkRowsFromFile,
  validateSuperAdminBulkCsv,
  validateSuperAdminBulkExcel,
} from './super-admin-recipe-bulk-upload.util';
import {
  BulkIngredientSearchHit,
  enrichSuperAdminBulkRowsFromIngredients,
  mapBulkIngredientNutrientRows,
  countNonCoreBulkNutrientRows,
  mergeBulkNutrientRows,
} from './super-admin-recipe-bulk-ingredient-enricher';
import { SuperAdminRecipeBulkUploadRowEditorComponent } from './super-admin-recipe-bulk-upload-row-editor.component';

function sanitizeDataImageUrl(value: string): string | null {
  const str = String(value || '').trim();
  if (!str.toLowerCase().startsWith('data:image/')) return null;
  const commaIdx = str.indexOf(',');
  if (commaIdx === -1) return null;
  const prefix = str.substring(0, commaIdx);
  const rawB64 = str.substring(commaIdx + 1).replace(/[\s\r\n\t]+/g, '');
  if (!rawB64 || rawB64.length < 50) return null;
  if (prefix.toLowerCase().includes(';base64')) {
    // Base64 string length modulo 4 can never be 1 in a valid encoding
    if (rawB64.length % 4 === 1) {
      return null;
    }
    let b64 = rawB64;
    const remainder = b64.length % 4;
    if (remainder === 2) b64 += '==';
    else if (remainder === 3) b64 += '=';

    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(b64)) {
      return null;
    }
    try {
      if (typeof window !== 'undefined' && typeof window.atob === 'function') {
        window.atob(b64);
      }
    } catch {
      return null;
    }
    return `${prefix},${b64}`;
  }
  return str;
}

@Component({
  selector: 'app-super-admin-recipe-bulk-upload-modal',
  templateUrl: './super-admin-recipe-bulk-upload-modal.component.html',
  styleUrls: ['./super-admin-recipe-bulk-upload-modal.component.scss'],
})
export class SuperAdminRecipeBulkUploadModalComponent implements OnInit, AfterViewInit {
  /** Super Admin bulk upload for RDMS recipes. */
  readonly statusOptions = SUPER_ADMIN_RECIPE_STATUS_OPTIONS.map((s) => ({ value: s, label: s }));
  readonly assignmentPriorities = ['High', 'Medium', 'Low'];

  @ViewChild('assignModalTpl') assignModalTpl!: TemplateRef<unknown>;

  step: 'upload' | 'preview' = 'upload';
  loadingLookups = false;
  parsing = false;
  enrichingIngredients = false;
  enrichProgress = '';
  fileName = '';
  fileError = '';
  parseResult: SuperAdminBulkParseResult | null = null;
  globalStatus = 'Draft';
  lookups: SuperAdminBulkLookups = {
    healthColorCodes: [],
    dietaryPreferences: [],
    mealTypes: [],
    sources: [],
    recipeCategories: [],
    mealPreferences: [],
    foodGroups: [],
    faoFoodGroups: [],
    cookingMethods: [],
    utensils: [],
    equipment: [],
    tastes: [],
    textures: [],
    flavours: [],
    aromas: [],
    continents: [],
    occasions: [],
    locations: [],
    seasons: [],
    allergens: [],
    allergenFree: [],
    intolerances: [],
    intoleranceFree: [],
    diseases: [],
    courses: [],
    storageTypes: [],
    reheatMethods: [],
  };

  // Assignment dialog state
  assignMode: 'maker-and-checker' | 'checker-only' = 'maker-and-checker';
  assignTargetRows: SuperAdminBulkPreviewRow[] = [];
  assignDialogIsBulk = false;
  selectedMakerEmail = '';
  selectedCheckerEmail = '';
  selectedDueDate = '';
  selectedPriority = 'Medium';
  selectedNotes = '';
  makerList: any[] = [];
  checkerList: any[] = [];
  isMakerLoading = false;
  isCheckerLoading = false;
  private assignModalRef: NgbModalRef | null = null;
  private statusBeforeAssign = 'Draft';
  private lastNonAssignGlobalStatus = 'Draft';
  private assigningGlobal = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private masterService: MasterService,
    private userService: UserService,
    private fssaiGeneratedTagService: FssaiGeneratedTagService
  ) {}

  get subtitle(): string {
    return this.step === 'upload'
      ? 'Upload CSV or Excel (all 237-field columns accepted). Ingredients are matched to master for nutrition, Health Color, Dietary Preference, and FSSAI tags. No fields are required — import as Draft anytime.'
      : 'Review rows, optionally configure dropdowns, set status (default Draft), then import valid rows.';
  }

  get validCount(): number {
    return this.parseResult?.validCount || 0;
  }

  get errorCount(): number {
    return this.parseResult?.errorCount || 0;
  }

  get totalCount(): number {
    return this.parseResult?.totalCount || 0;
  }

  get assignTargetRecipe(): SuperAdminBulkPreviewRow | null {
    return !this.assignDialogIsBulk && this.assignTargetRows.length === 1
      ? this.assignTargetRows[0]
      : null;
  }

  async ngOnInit(): Promise<void> {
    await this.loadLookups();
    this.scheduleLucideIconRender();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
  }

  downloadTemplate(format: SuperAdminRecipeTemplateFormat = 'csv'): void {
    downloadSuperAdminRecipeTemplate(format);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isCsv = lowerName.endsWith('.csv');
    const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');
    if (!isCsv && !isExcel) {
      this.fileError = 'Please upload a valid CSV or Excel (.xlsx) file.';
      input.value = '';
      return;
    }

    this.fileError = '';
    this.fileName = file.name;
    this.parsing = true;

    const applyParseResult = async (result: SuperAdminBulkParseResult): Promise<void> => {
      this.parseResult = result;
      if (this.parseResult.fileError) {
        this.fileError = this.parseResult.fileError;
        this.step = 'upload';
        return;
      }
      if (!this.parseResult.totalCount) {
        this.fileError = 'No data rows found in the file.';
        this.step = 'upload';
        return;
      }

      applyGlobalStatusToRows(this.parseResult.rows, this.globalStatus);
      hydrateAllSuperAdminBulkRowsFromFile(this.parseResult.rows, this.lookups);
      this.step = 'preview';
      this.refreshAllRows();
      await this.enrichIngredientsFromMaster();
      // Re-apply file→master matches after enrich (lookups guaranteed; don't wipe enrich fills).
      hydrateAllSuperAdminBulkRowsFromFile(this.parseResult.rows, this.lookups);
      this.refreshAllRows();
    };

    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        try {
          if (isCsv) {
            await applyParseResult(validateSuperAdminBulkCsv(String(reader.result || '')));
          } else {
            await applyParseResult(validateSuperAdminBulkExcel(reader.result as ArrayBuffer));
          }
        } catch (err) {
          console.error('Failed to parse super admin bulk upload file', err);
          this.fileError = 'Failed to parse CSV or Excel file.';
          this.step = 'upload';
        } finally {
          this.parsing = false;
          this.scheduleLucideIconRender();
        }
      })();
    };
    reader.onerror = () => {
      this.parsing = false;
      this.fileError = 'Failed to read file.';
    };
    if (isCsv) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
    input.value = '';
  }

  backToUpload(): void {
    this.step = 'upload';
    this.parseResult = null;
    this.fileName = '';
    this.fileError = '';
    this.scheduleLucideIconRender();
  }

  onGlobalStatusChange(): void {
    if (!this.parseResult?.rows?.length) return;
    const next = this.globalStatus;

    if (this.isAssignmentStatus(next)) {
      this.statusBeforeAssign = this.lastNonAssignGlobalStatus || 'Draft';
      applyGlobalStatusToRows(this.parseResult.rows, next);
      this.refreshAllRows();
      void this.openAssignDialog(this.parseResult.rows, next, true);
      return;
    }

    this.lastNonAssignGlobalStatus = next;
    applyGlobalStatusToRows(this.parseResult.rows, next);
    this.refreshAllRows();
  }

  onRowStatusChange(row: SuperAdminBulkPreviewRow): void {
    const next = row.selections.status;
    if (this.isAssignmentStatus(next)) {
      this.statusBeforeAssign = 'Draft';
      if (
        next === 'Assigned to Maker' &&
        (!row.selections.assignment?.makerEmail || !row.selections.assignment?.checkerEmail)
      ) {
        row.selections.assignment = null;
      }
      if (next === 'Assigned to Checker' && !row.selections.assignment?.checkerEmail) {
        row.selections.assignment = null;
      }
      rebuildSuperAdminRecipePayload(row, this.lookups);
      refreshSuperAdminParseCounts(this.parseResult!);
      void this.openAssignDialog([row], next, false);
      return;
    }

    row.selections.assignment = null;
    rebuildSuperAdminRecipePayload(row, this.lookups);
    refreshSuperAdminParseCounts(this.parseResult!);
  }

  private isAssignmentStatus(status: string): boolean {
    return status === 'Assigned to Maker' || status === 'Assigned to Checker';
  }

  private async openAssignDialog(
    rows: SuperAdminBulkPreviewRow[],
    status: string,
    isBulk: boolean
  ): Promise<void> {
    this.assignMode = status === 'Assigned to Checker' ? 'checker-only' : 'maker-and-checker';
    this.assignTargetRows = rows;
    this.assignDialogIsBulk = isBulk || rows.length > 1;
    this.assigningGlobal = isBulk;

    const existing = rows[0]?.selections?.assignment;
    this.selectedMakerEmail = existing?.makerEmail || '';
    this.selectedCheckerEmail = existing?.checkerEmail || '';
    this.selectedDueDate = existing?.dueDate || '';
    this.selectedPriority = existing?.priority || 'Medium';
    this.selectedNotes = existing?.notes || '';

    if (this.assignMode === 'maker-and-checker') {
      void this.loadRoleUserList('maker');
      void this.loadRoleUserList('checker');
    } else {
      void this.loadRoleUserList('checker');
    }

    try {
      this.assignModalRef?.dismiss('reopen');
    } catch {
      /* noop */
    }

    this.assignModalRef = this.modalService.open(this.assignModalTpl, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: true,
      windowClass: 'recipe-assign-modal-window saru-assign-window',
    });
    this.scheduleLucideIconRender();

    this.assignModalRef.result.then(
      () => {
        this.assignModalRef = null;
      },
      (reason) => {
        if (reason !== 'assigned') {
          this.onAssignCancelled();
        }
        this.assignModalRef = null;
      }
    );
  }

  private onAssignCancelled(): void {
    if (this.assigningGlobal && this.parseResult?.rows?.length) {
      this.globalStatus = this.statusBeforeAssign || 'Draft';
      applyGlobalStatusToRows(this.parseResult.rows, this.globalStatus);
      this.refreshAllRows();
    } else if (this.assignTargetRows.length === 1) {
      const row = this.assignTargetRows[0];
      if (!row.selections.assignment) {
        row.selections.status = this.statusBeforeAssign || 'Draft';
        row.selections.assignment = null;
        rebuildSuperAdminRecipePayload(row, this.lookups);
        refreshSuperAdminParseCounts(this.parseResult!);
      }
    }
    this.assignTargetRows = [];
    this.assigningGlobal = false;
  }

  confirmAssignment(modal?: { close: (r?: string) => void }): void {
    if (this.assignMode === 'maker-and-checker') {
      if (!this.selectedMakerEmail || !this.selectedCheckerEmail) return;
    } else if (!this.selectedCheckerEmail) {
      return;
    }

    const assignment: SuperAdminBulkAssignment = {
      mode: this.assignMode,
      makerEmail: this.assignMode === 'maker-and-checker' ? this.selectedMakerEmail : null,
      checkerEmail: this.selectedCheckerEmail,
      dueDate: this.selectedDueDate || null,
      priority: this.selectedPriority || 'Medium',
      notes: this.selectedNotes || null,
    };

    for (const row of this.assignTargetRows) {
      row.selections.assignment = { ...assignment };
      row.selections.status =
        this.assignMode === 'maker-and-checker' ? 'Assigned to Maker' : 'Assigned to Checker';
      rebuildSuperAdminRecipePayload(row, this.lookups);
    }

    if (this.assigningGlobal) {
      this.globalStatus =
        this.assignMode === 'maker-and-checker' ? 'Assigned to Maker' : 'Assigned to Checker';
    }

    refreshSuperAdminParseCounts(this.parseResult!);
    modal?.close('assigned');
    this.assignModalRef = null;
    this.assignTargetRows = [];
    this.assigningGlobal = false;
    this.scheduleLucideIconRender();
  }

  closeAssignDialog(modal?: { dismiss: (r?: string) => void }): void {
    modal?.dismiss('cancel');
  }

  getAssignTargetCount(): number {
    return this.assignTargetRows.length;
  }

  private async loadRoleUserList(role: 'maker' | 'checker'): Promise<void> {
    const isMaker = role === 'maker';
    if (isMaker) {
      this.isMakerLoading = true;
      this.makerList = [];
    } else {
      this.isCheckerLoading = true;
      this.checkerList = [];
    }

    try {
      const res: any = await this.userService.getUsers({
        pageNo: 1,
        pageSize: 200,
        searchString: '',
        roleId: isMaker ? 5 : 4,
      });
      if (res && res.status === 0 && res.response) {
        const users = res.response.userList || res.response.data || res.response || [];
        if (isMaker) this.makerList = users;
        else this.checkerList = users;
      }
    } catch (error) {
      console.error(`Failed to load ${role} list:`, error);
    } finally {
      if (isMaker) this.isMakerLoading = false;
      else this.isCheckerLoading = false;
    }
  }

  async onRowImageSelected(event: Event, row: SuperAdminBulkPreviewRow): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      this.fileError = `Invalid image type for row #${row.rowNumber}. Use PNG, JPEG, or WebP.`;
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.fileError = `Image too large for row #${row.rowNumber}. Max 5MB.`;
      input.value = '';
      return;
    }

    this.fileError = '';
    row.selections.thumbnailUploading = true;
    try {
      const url = await this.masterService.uploadMedia(file, 'recipe-media');
      if (url) {
        row.selections.thumbnailImagePath = url;
        rebuildSuperAdminRecipePayload(row, this.lookups);
        refreshSuperAdminParseCounts(this.parseResult!);
      } else {
        this.fileError = `Failed to upload image for row #${row.rowNumber}.`;
      }
    } catch (err) {
      console.error('Row image upload failed', err);
      this.fileError = `Failed to upload image for row #${row.rowNumber}.`;
    } finally {
      row.selections.thumbnailUploading = false;
      input.value = '';
      this.scheduleLucideIconRender();
    }
  }

  removeRowImage(row: SuperAdminBulkPreviewRow): void {
    row.selections.thumbnailImagePath = null;
    rebuildSuperAdminRecipePayload(row, this.lookups);
    refreshSuperAdminParseCounts(this.parseResult!);
    this.scheduleLucideIconRender();
  }

  getRowThumbnailUrl(row: SuperAdminBulkPreviewRow): string | null {
    const raw = String(row?.selections?.thumbnailImagePath || '').trim();
    if (
      !raw ||
      raw.toLowerCase() === 'string' ||
      raw.toLowerCase() === 'null' ||
      raw.toLowerCase() === 'undefined'
    ) {
      return null;
    }
    if (/^data:image\//i.test(raw)) {
      return sanitizeDataImageUrl(raw);
    }
    if (/^blob:/i.test(raw)) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('//')) return `https:${raw}`;
    if (raw.startsWith('assets/')) return raw;
    const base = (environment.baseUrl || '').replace(/\/$/, '');
    if (raw.startsWith('/')) return base ? `${base}${raw}` : raw;
    if (raw.includes('/')) return base ? `${base}/${raw.replace(/^\//, '')}` : raw;
    return base ? `${base}/uploads/recipe-media/${encodeURIComponent(raw)}` : raw;
  }

  openRowEditor(row: SuperAdminBulkPreviewRow): void {
    const modalRef = this.modalService.open(SuperAdminRecipeBulkUploadRowEditorComponent, {
      size: 'xl',
      backdrop: 'static',
      centered: true,
      windowClass: 'super-admin-bulk-row-editor-window',
    });
    modalRef.componentInstance.row = row;
    modalRef.componentInstance.lookups = this.lookups;

    modalRef.result
      .then((updated: SuperAdminBulkPreviewRow) => {
        if (!updated) return;
        rebuildSuperAdminRecipePayload(updated, this.lookups);
        refreshSuperAdminParseCounts(this.parseResult!);
        if (this.isAssignmentStatus(updated.selections.status) && !updated.selections.assignment) {
          void this.openAssignDialog([updated], updated.selections.status, false);
        }
        this.scheduleLucideIconRender();
      })
      .catch(() => {});
  }

  deleteRow(index: number): void {
    if (!this.parseResult?.rows?.length) return;
    this.parseResult.rows.splice(index, 1);
    this.parseResult.rows.forEach((row, i) => {
      row.rowNumber = i + 1;
    });
    if (!this.parseResult.rows.length) {
      this.backToUpload();
      return;
    }
    this.refreshAllRows();
    this.scheduleLucideIconRender();
  }

  confirmImport(): void {
    const validItems: SuperAdminBulkImportItem[] = (this.parseResult?.rows || [])
      .filter((r) => r.valid && r.payload)
      .map((r) => ({
        payload: r.payload as Record<string, unknown>,
        recipeUoms: r.selections.recipeUoms || [],
        assignment: r.selections.assignment || null,
      }));
    if (!validItems.length) return;
    this.activeModal.close(validItems);
  }

  private refreshAllRows(): void {
    if (!this.parseResult) return;
    this.parseResult.rows.forEach((row) => {
      rebuildSuperAdminRecipePayload(row, this.lookups);
    });
    refreshSuperAdminParseCounts(this.parseResult);
  }

  private async enrichIngredientsFromMaster(): Promise<void> {
    if (!this.parseResult?.rows?.length) return;
    this.enrichingIngredients = true;
    this.enrichProgress = 'Matching ingredients to master…';
    try {
      await enrichSuperAdminBulkRowsFromIngredients(
        this.parseResult.rows,
        this.lookups,
        {
          bulkResolveIngredients: async (names) => this.resolveIngredientNamesBulk(names),
          searchIngredients: async (term) => {
            const res: any = await this.masterService.searchIngredientsForRecipe(term, 1, 10);
            const records = this.extractPgData<any>(res);
            return records.map((i: any) => this.mapIngredientSearchHit(i));
          },
          getServingUnits: async () => [],
          calculateFssaiTags: undefined,
          getIngredientNutrientRows: undefined,
          getIngredientDetail: undefined,
        },
        (done, total) => {
          this.enrichProgress =
            done === 0
              ? `Resolving ${total} unique ingredients (1 request)…`
              : done >= total
                ? `Matched ${total} unique ingredients`
                : `Matching ingredients… ${done}/${total}`;
        },
        {
          fast: true,
          concurrency: 12,
          calculateFssai: false,
          fetchServingUnits: false,
          fetchDetail: false,
        }
      );
    } catch (err) {
      console.error('Failed to enrich bulk upload ingredients', err);
    } finally {
      this.enrichingIngredients = false;
      this.enrichProgress = '';
      this.scheduleLucideIconRender();
    }
  }

  /** One API round-trip for many ingredient names (bulk upload). */
  private async resolveIngredientNamesBulk(
    names: string[]
  ): Promise<Map<string, BulkIngredientSearchHit | null>> {
    const map = new Map<string, BulkIngredientSearchHit | null>();
    const unique = Array.from(
      new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))
    );
    if (!unique.length) return map;

    // Backend accepts up to 2000; chunk defensively for very large files.
    const chunkSize = 800;
    for (let i = 0; i < unique.length; i += chunkSize) {
      const chunk = unique.slice(i, i + chunkSize);
      const res: any = await this.masterService.resolveIngredientsByNames(chunk);
      const payload = res?.data ?? res?.response ?? res?.Response ?? res;
      const matches = Array.isArray(payload?.matches)
        ? payload.matches
        : Array.isArray(payload?.Matches)
          ? payload.Matches
          : [];

      for (const m of matches) {
        const query = String(m?.queryName ?? m?.QueryName ?? '').trim();
        if (!query) continue;
        const key = query.toLowerCase().replace(/\s+/g, ' ');
        const matched = !!(m?.matched ?? m?.Matched);
        if (!matched) {
          map.set(key, null);
          continue;
        }
        map.set(key, {
          id: Number(m?.id ?? m?.Id ?? m?.ingredientMasterId ?? m?.IngredientMasterId ?? 0),
          ingredientRefId:
            Number(m?.ingredientRefId ?? m?.IngredientRefId ?? 0) || null,
          name:
            m?.ingredientName ||
            m?.IngredientName ||
            m?.commonName ||
            m?.CommonName ||
            query,
          calories: Number(m?.calories ?? m?.Calories ?? 0) || 0,
          protein: Number(m?.protein ?? m?.Protein ?? 0) || 0,
          fat: Number(m?.fat ?? m?.Fat ?? 0) || 0,
          carbs: Number(m?.carbs ?? m?.Carbs ?? 0) || 0,
          fiber: Number(m?.fiber ?? m?.Fiber ?? 0) || 0,
          quantity: Number(m?.quantity ?? m?.Quantity ?? 100) || 100,
          isVeg: m?.isVeg ?? m?.IsVeg ?? null,
          mealPreference: String(m?.mealPreference || m?.MealPreference || ''),
          healthColorCodeId: Number(m?.healthColorCodeId ?? m?.HealthColorCodeId ?? 0) || null,
          healthColorCodeName: String(
            m?.healthColorCodeName || m?.HealthColorCodeName || ''
          ),
          colorCode: Number(m?.colorCode ?? m?.ColorCode ?? 0) || null,
        });
      }
    }
    return map;
  }

  private mapIngredientSearchHit(i: any): BulkIngredientSearchHit {
    return {
      id: Number(i.id ?? i.Id ?? i.ingredientMasterId ?? i.IngredientMasterId),
      ingredientRefId: Number(i.ingredientRefId ?? i.IngredientRefId ?? 0) || null,
      name: i.ingredientName || i.IngredientName || i.commonName || i.CommonName || 'Unknown',
      calories: Number(i.calories ?? i.Calories ?? 0) || 0,
      protein: Number(i.protein ?? i.Protein ?? 0) || 0,
      fat: Number(i.fat ?? i.Fat ?? 0) || 0,
      carbs: Number(i.carbs ?? i.Carbs ?? 0) || 0,
      fiber: Number(i.fiber ?? i.Fiber ?? 0) || 0,
      quantity: Number(i.quantity ?? i.Quantity ?? 100) || 100,
      isVeg: i.isVeg ?? i.IsVeg ?? null,
      mealPreference: String(i.mealPreference || i.MealPreference || i.dietaryPreference || ''),
      healthColorCodeId: Number(i.healthColorCodeId ?? i.HealthColorCodeId ?? 0) || null,
      healthColorCodeName: String(
        i.healthColorCodeName || i.HealthColorCodeName || i.healthColorCodeLabel || ''
      ),
      colorCode: Number(i.colorCode ?? i.ColorCode ?? 0) || null,
    };
  }

  private async loadIngredientNutrientRows(ingredientId: number): Promise<
    Array<{ nutrition: string; value: number | string; unit: string }>
  > {
    const id = Number(ingredientId) || 0;
    if (id <= 0) return [];

    let fromDetail: Array<{ nutrition: string; value: number | string; unit: string }> = [];
    try {
      const res: any = await this.masterService.getIngredientById(String(id), 'RDMS');
      const detail = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? res;
      const nutrientInfo = detail?.nutrientInformation ?? detail?.NutrientInformation ?? [];
      fromDetail = mapBulkIngredientNutrientRows(
        Array.isArray(nutrientInfo) ? nutrientInfo : []
      );
    } catch {
      fromDetail = [];
    }

    let rows = fromDetail;
    if (countNonCoreBulkNutrientRows(fromDetail) === 0) {
      try {
        const res: any = await this.masterService.getIngredientNutritionParams(String(id), 'RDMS');
        const payload = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? res;
        const list = Array.isArray(payload)
          ? payload
          : payload?.data ?? payload?.records ?? payload?.Records ?? payload?.items ?? [];
        const fromParams = mapBulkIngredientNutrientRows(Array.isArray(list) ? list : []);
        rows = mergeBulkNutrientRows(fromDetail, fromParams);
      } catch {
        // keep fromDetail
      }
    }
    return rows;
  }

  private extractPgData<T>(res: any): T[] {
    const data = res?.data ?? res?.response ?? res?.Response ?? res;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.records)) return data.records;
    return [];
  }

  private async loadLookups(): Promise<void> {
    this.loadingLookups = true;
    try {
      const [meta, continentMeta] = await Promise.all([
        this.masterService.getRecipeMetadata(SUPER_ADMIN_RECIPE_METADATA_INCLUDE),
        this.masterService.getRecipeMetadata('cuisineContinents'),
      ]);
      let mapped = mapMetadataToLookups(meta, continentMeta);

      if (!this.hasUsableLookups(mapped)) {
        const fullMeta = await this.masterService.getRecipeMetadata();
        mapped = mapMetadataToLookups(fullMeta, continentMeta);
      }

      this.lookups = mapped;
    } catch (err) {
      console.error('Failed to load super admin bulk upload lookups', err);
    } finally {
      this.loadingLookups = false;
    }
  }

  private hasUsableLookups(lookups: SuperAdminBulkLookups): boolean {
    return !!(
      lookups.healthColorCodes.length ||
      lookups.mealTypes.length ||
      lookups.sources.length ||
      lookups.occasions.length ||
      lookups.locations.length ||
      lookups.seasons.length ||
      lookups.storageTypes.length ||
      lookups.reheatMethods.length
    );
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
